import { Destroyable } from 'app/shared/utilities/Destructor';

export interface IEventRegistry {
    addEventListener(fn: Function): () => any;
    notifyListeners(...args: Array<any>): Promise<any>;
}

/**
 * A helper
 */
export class EventRegistry<T extends any[] = any[]> implements Destroyable {
    _initialArgs: T | null;
    _listeners: Set<(...args: T) => any>;
    /**
     * Any arguments passed into the constructor will be saved and used
     * to call any added event listener upon registration
     */
    constructor(...args: any[]) {
        // will save any args passed in.
        this._initialArgs = args.length ? (args as T) : null;
        this._listeners = new Set();
    }

    /**
     * Add an event listener function
     * @param listenerFunc
     * @returns {function()} A function to remove the event listener from the registry.
     * Similar to how $rootScope does event registration
     */
    addEventListener(listenerFunc: (...args: T) => any): () => void {
        this._listeners.add(listenerFunc);
        if (this._initialArgs) {
            // Only apply the initial args to the newly added listener
            try {
                listenerFunc.apply(this, this._initialArgs);
            } catch (e) {
                console.error('Event register error while doing initial listener callback', e);
            }
        }
        return () => this._listeners.delete(listenerFunc);
    }

    /**
     * Notify all of the registered event listeners. Any passed in
     * arguments will be sent to the listeners
     * @returns A promise with the results from calling all of the event handlers
     */
    notifyListeners(...args: T): Promise<any[]> {
        return Promise.all(
            [...this._listeners].map((l) => {
                // If one of the listeners throws an uncaught exception we can get strange behaviour
                // which is hard to trace. Catching here is nice to solve the traceability issue.
                try {
                    return l.apply(this, args);
                } catch (e) {
                    console.error('Event register error while doing listener callback', e);
                    return null;
                }
            }),
        );
    }

    /**
     * Without question, destroys all listeners
     */
    destroyAllListeners() {
        this._listeners.clear();
    }

    destroy(): any {
        // just implementing the destroyable interface
        this.destroyAllListeners();
    }
}
